/**
 * chain-eos/tokens-sort.ts — THE token order. One function, used everywhere.
 *
 * The order kept drifting because every screen sorted its own way. It is now
 * defined once, frozen by a test, and every token list in the app must call
 * `sortTokensForDisplay`. Do not sort token arrays anywhere else.
 *
 * Tiers, in order:
 *   0. Priority tokens — Vaulta (A), EOS, USDT, in TOKEN_DISPLAY_ORDER.
 *      Native first, always, regardless of balance.
 *   1. PayCash ecosystem tokens (token.pcash, swap.pcash, …).
 *   2. Everything else that has a logo.
 *   3. Everything else without a logo.
 *
 * Inside a tier: bigger USD value first, then bigger balance, then symbol A→Z so
 * the list never reshuffles between renders when values tie.
 */

import { PAYCASH_TOKEN_CONTRACTS, TOKEN_DISPLAY_ORDER } from './config.js'
import type { TokenBalance } from './balance.js'

export const TOKEN_TIER = {
  priority: 0,
  paycash: 1,
  withLogo: 2,
  plain: 3,
} as const

/** Lower is higher in the list. Priority tokens keep their TOKEN_DISPLAY_ORDER position. */
export function tokenSortRank(token: Pick<TokenBalance, 'symbol' | 'contract' | 'logoUrl'>): number {
  const priorityIndex = TOKEN_DISPLAY_ORDER.indexOf(token.symbol as (typeof TOKEN_DISPLAY_ORDER)[number])
  if (priorityIndex >= 0) return TOKEN_TIER.priority * 100 + priorityIndex
  if ((PAYCASH_TOKEN_CONTRACTS as readonly string[]).includes(token.contract)) {
    return TOKEN_TIER.paycash * 100
  }
  if (token.logoUrl) return TOKEN_TIER.withLogo * 100
  return TOKEN_TIER.plain * 100
}

/** Returns a new array — never mutates the input. */
export function sortTokensForDisplay<T extends TokenBalance>(tokens: T[]): T[] {
  return [...tokens].sort((a, b) => {
    const rank = tokenSortRank(a) - tokenSortRank(b)
    if (rank !== 0) return rank
    const usd = (b.usdValue ?? 0) - (a.usdValue ?? 0)
    if (usd !== 0) return usd
    const amount = b.amountRaw - a.amountRaw
    if (amount !== 0) return amount
    return a.symbol.localeCompare(b.symbol)
  })
}
